import React, { useState, useEffect, useRef } from "react";
import { User, CheckCircle2, XCircle, Loader2, Camera, Save, RotateCcw } from "lucide-react";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, getValidToken } from "../../../apiClient";

// Cloudinary Helper
const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", "juxso4ls");
  const res = await fetch("https://api.cloudinary.com/v1_1/mayur28/image/upload", {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw new Error("Cloudinary upload failed");
  return res.json();
};

const Profile = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    gender: "",
    image: "",
  });

  // Validation State
  const [usernameStatus, setUsernameStatus] = useState("idle"); // idle, checking, available, taken
  const [originalUsername, setOriginalUsername] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");

  // 1. Fetch Profile Data
  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { userid } = await getValidToken();
      const res = await apiClient(`/user/getprofile/${userid}`);
      return res.profile;
    },
    onSuccess: (data) => {
      if (data) {
        setFormData({
          name: data.name || "",
          username: data.username || "",
          gender: data.gender || "male",
          image: data.image || "",
        });
        setOriginalUsername(data.username || "");
        setAvatarPreview(data.image || "");
      }
    }
  });

  // Keep state in sync when query loads
  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || "",
        username: profile.username || "",
        gender: profile.gender || "male",
        image: profile.image || "",
      });
      setOriginalUsername(profile.username || "");
      setAvatarPreview(profile.image || "");
    }
  }, [profile]);

  // 2. Handle Username Changes
  const handleUsernameChange = (e) => {
    const val = e.target.value.toLowerCase().replace(/\s/g, ''); // Keep it url-safe
    setFormData({ ...formData, username: val });
    
    if (val === originalUsername) {
      setUsernameStatus("idle");
    } else if (val.length < 5) {
      setUsernameStatus("invalid");
    } else {
      setUsernameStatus("unverified");
    }
  };

  const verifyUsername = async () => {
    if (formData.username.length < 5) return;
    setUsernameStatus("checking");
    try {
      await getValidToken();
      const res = await fetch(`https://orchestration.trackdsa.in/user/validateusername/${formData.username}`);
      const data = await res.json();
      setUsernameStatus(data.verified ? "available" : "taken");
    } catch (err) {
      toast.error("Failed to verify username");
      setUsernameStatus("unverified");
    }
  };

  // 3. Handle Avatar Selection
  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB");
      return;
    }

    setAvatarFile(file);
    // Create local preview instantly
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  // 4. Submit Update Mutation
  const { mutate: updateProfile, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      let finalImageUrl = formData.image;

      // Upload to Cloudinary if new file selected
      if (avatarFile) {
        const cloudinaryRes = await uploadToCloudinary(avatarFile);
        finalImageUrl = cloudinaryRes.secure_url;
      }

      const { userid } = await getValidToken();
      const payload = { ...formData, image: finalImageUrl, userid };

      return apiClient(`/user/updateprofile/${userid}`, {
        method: "PUT",
        body: JSON.stringify(payload)
      });
    },
    onSuccess: (data) => {
      toast.success("Profile updated successfully!");
      
      // Update local cookies
      Cookies.set("profileName", data.profile.name, { expires: 7 });
      Cookies.set("profileImage", data.profile.image, { expires: 7 });
      
      queryClient.invalidateQueries(["profile"]);
      setAvatarFile(null); // Reset staged file
      setOriginalUsername(data.profile.username);
      setUsernameStatus("idle");
      
      // Force reload to update navbar avatar
      window.location.reload();
    },
    onError: (err) => toast.error(err.message || "Failed to update profile")
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (usernameStatus === "unverified" || usernameStatus === "taken" || usernameStatus === "invalid") {
      toast.error("Please provide a valid, verified username");
      return;
    }
    updateProfile();
  };

  if (isLoading) {
    return (
      <div className="p-8 space-y-6 animate-pulse">
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 bg-slate-200 rounded-full" />
          <div className="w-48 h-6 bg-slate-200 rounded" />
        </div>
        <div className="space-y-4">
          <div className="w-full h-10 bg-slate-200 rounded-lg" />
          <div className="w-full h-10 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      <form onSubmit={handleSubmit} className="max-w-xl space-y-6">
        
        {/* Avatar Upload */}
        <div className="flex items-center gap-6 pb-6 border-b border-slate-100">
          <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleAvatarSelect} 
              accept="image/png, image/jpeg, image/jpg" 
              className="hidden" 
            />
            <div className="w-24 h-24 rounded-full border-4 border-white shadow-md overflow-hidden bg-slate-100 flex items-center justify-center">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-slate-300" />
              )}
            </div>
            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">Profile Picture</h3>
            <p className="text-sm text-slate-500">JPG or PNG. Max size of 5MB.</p>
            <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700">
              Change Image
            </button>
          </div>
        </div>

        {/* Inputs */}
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">@</span>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleUsernameChange}
                  className="w-full pl-8 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                />
              </div>
              <button
                type="button"
                onClick={verifyUsername}
                disabled={usernameStatus === "idle" || usernameStatus === "invalid" || usernameStatus === "checking"}
                className="px-4 py-2 bg-slate-100 text-slate-600 font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50 transition-colors text-sm"
              >
                Verify
              </button>
            </div>
            
            {/* Username Validation Messages */}
            <div className="mt-2 flex items-center text-sm">
              {usernameStatus === "checking" && <span className="text-slate-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin"/> Checking availability...</span>}
              {usernameStatus === "available" && <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4"/> Username available!</span>}
              {usernameStatus === "taken" && <span className="text-red-500 flex items-center gap-1"><XCircle className="w-4 h-4"/> Username is taken</span>}
              {usernameStatus === "invalid" && <span className="text-amber-500 text-xs">Must be at least 5 characters</span>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
            <select
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="pt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            <Save className="w-4 h-4" />
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
          <button
            type="button"
            onClick={() => {
              setFormData(profile);
              setAvatarPreview(profile.image || "");
              setAvatarFile(null);
              setUsernameStatus("idle");
            }}
            className="flex items-center gap-2 px-4 py-2.5 text-slate-600 font-medium rounded-lg hover:bg-slate-100 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>

      </form>
    </div>
  );
};

export default Profile;